import React from "react";
import { useState } from "react";
import {
  customUrlAllowedCharRegex,
  ProcedureConsts,
  ProcedureEntity,
  procedureIdFormatRegex,
  ProcedureType,
} from "../../../../../application/domain/procedures/procedure-entity";
import { useAuthContextV2 } from "../../../../../auth-state";
import {
  SmartApplyApiError,
  ToastMessage,
  reportAsyncError,
  reportUnexpectedError,
} from "@graffer-inc/smart-apply";
import { gqlClient, gqlHeader, runGqlQueryV2 } from "@graffer-inc/smart-apply";
import apiClient from "../../../../../application/apis/api-client";
import { toast } from "react-toastify";

type CustomPath = {
  customPath: string;
  orgId: string;
  procedureId: string;
  startUnixtime: number;
};

export const validatePath = (
  newPath: string,
  currentPath?: string,
): string | undefined => {
  if (newPath.length === 0 || !newPath.match(customUrlAllowedCharRegex)) {
    return "半角英数字、ハイフン(-)で入力してください";
  }
  if (newPath.match(procedureIdFormatRegex)) {
    return "手続きIDと同じ19桁数字の形式は使用できません";
  }
  if (newPath.length > ProcedureConsts.MAX_CUSTOM_PATH_LENGTH) {
    return "64文字以内で入力してください";
  }
  if (currentPath && currentPath === newPath) {
    return "現在の申請開始ページのURLと同じです";
  }
  return undefined;
};

export const validateStartDatetime = (value?: Date): string | undefined => {
  if (!value) {
    return "反映日時を選択してください";
  }
  const now = new Date();
  if (value && now.getTime() > value.getTime()) {
    return "過去の日時を選択することはできません";
  }
  return undefined;
};

export const useAssignCustomPath = (
  orgId: string,
  procedureId: string,
  afterReallocate: (procedureId: string) => Promise<void>,
): {
  currentPaths: {
    current?: CustomPath;
    next?: CustomPath;
  };
  pageStatus: "EDIT" | "CONFIRM";
  newPath: string;
  newStartDatetime?: Date;
  isLoading: boolean;
  onChangePath: (path: string) => void;
  onChangeStartDate: (startDate?: Date) => void;
  onConfirm: () => Promise<void>;
  onBackToEdit: () => void;
  onReallocateCustomPath: (
    customPath: string,
    startDatetime?: Date,
  ) => Promise<void>;
  onDeleteReservedCustomPath: () => Promise<void>;
  usingPathProcName: string | undefined;
  canAssign: boolean | undefined;
  reservedPathProcName: string | undefined;
  isReserved: boolean;
} => {
  const [currentPaths, setCurrentPaths] = useState<{
    current?: CustomPath;
    next?: CustomPath;
  }>({});
  const { auth } = useAuthContextV2();
  const [newPath, setNewPath] = useState("");
  const [newStartDatetime, setNewStartDatetime] = useState<Date | undefined>();
  const [usingPathProc, setUsingPathProc] = useState<
    ProcedureEntity | undefined
  >(undefined);
  const [reservedPathProc, setReservedPathProc] = useState<
    ProcedureEntity | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [pageStatus, setPageState] = useState<"EDIT" | "CONFIRM">("EDIT");

  const onChangePath = React.useCallback((path: string) => {
    setNewPath(path);
  }, []);
  const onChangeStartDate = React.useCallback((startDatetime?: Date) => {
    setNewStartDatetime(startDatetime);
  }, []);

  const fetchCustomPaths = React.useCallback(async () => {
    setIsLoading(true);
    const res = await runGqlQueryV2(() =>
      gqlClient.currentCustomPaths(
        { orgId: orgId, procedureId: procedureId },
        gqlHeader("q"),
      ),
    );
    if (res.error) {
      reportUnexpectedError(res.error.rawError);
      setIsLoading(false);
      return;
    }
    if (res.data) {
      const currentPaths = res.data.currentCustomPaths;
      setCurrentPaths({
        current: currentPaths.currentCustomPath
          ? { ...currentPaths.currentCustomPath }
          : undefined,
        next: currentPaths.nextCustomPath
          ? { ...currentPaths.nextCustomPath }
          : undefined,
      });
      setNewPath(currentPaths.nextCustomPath?.customPath ?? "");
      setNewStartDatetime(
        currentPaths.nextCustomPath
          ? new Date(currentPaths.nextCustomPath.startUnixtime * 1000)
          : undefined,
      );
    }
    setIsLoading(false);
  }, [orgId, procedureId]);

  const onConfirm = async () => {
    setIsLoading(true);

    const result = await runGqlQueryV2(() =>
      gqlClient.proceduresWithCustomPath(
        { path: newPath, orgId: orgId },
        gqlHeader("q"),
      ),
    );
    if (result.error) {
      reportAsyncError(result.error.rawError);
      setIsLoading(false);
      return;
    }

    const procUsingCustomPath = result.convert((e) => {
      if (!e.proceduresWithCustomPath?.procUsingCustomPath) return undefined;
      return ProcedureEntity.fromJSON(
        e.proceduresWithCustomPath?.procUsingCustomPath as ProcedureType,
      );
    });
    const procHasReservedCustomPath = result.convert((e) => {
      if (!e.proceduresWithCustomPath?.procHasReservedCustomPath)
        return undefined;
      return ProcedureEntity.fromJSON(
        e.proceduresWithCustomPath?.procHasReservedCustomPath as ProcedureType,
      );
    });
    setUsingPathProc(procUsingCustomPath.data);
    setReservedPathProc(procHasReservedCustomPath.data);
    setPageState("CONFIRM");
    setIsLoading(false);
  };

  const onBackToEdit = () => setPageState("EDIT");

  const canAssign =
    !usingPathProc ||
    (auth.isServiceProviderAccount()
      ? auth.hasPermissions("procedures.update")
      : auth.checkGroupPermission(usingPathProc, ["procedures.update"]));

  React.useEffect(() => {
    fetchCustomPaths();
  }, [fetchCustomPaths]);

  const onDeleteReservedCustomPath = React.useCallback(async () => {
    try {
      await apiClient.deleteReservedCustomPath(orgId, procedureId);
      fetchCustomPaths();
      await afterReallocate(procedureId);
      toast.success("申請開始ページURLの変更予約を取り消しました。");
    } catch (e) {
      if (SmartApplyApiError.isRetryable(e)) {
        ToastMessage.retryable().show();
        return;
      }
      toast.error(
        "予期せぬエラーが発生したため、取り消すことができませんでした。時間をおいてから、再度お試しください。",
      );
      reportUnexpectedError(e);
    }
  }, [orgId, procedureId, fetchCustomPaths, afterReallocate]);

  const onReallocateCustomPath = React.useCallback(
    async (customPath: string, startDatetime?: Date) => {
      try {
        await apiClient.assignCustomPath(
          procedureId,
          customPath,
          startDatetime,
        );
        await afterReallocate(procedureId);
        const successMessage = startDatetime
          ? "申請開始ページのURLを予約しました。"
          : "申請開始ページのURLを変更しました。";
        toast.success(successMessage);
      } catch (e) {
        if (SmartApplyApiError.isErrorCode(e, "custom_url_already_exists")) {
          toast.error(
            "別の手続きで、同じカスタムパスが同じ時間に設定されています。設定内容を変更して再設定してください。",
          );
          return;
        }
        if (SmartApplyApiError.isRetryable(e)) {
          ToastMessage.retryable().show();
          return;
        }
        toast.error(
          "予期せぬエラーが発生したため、変更することができませんでした。時間をおいてから、再度お試しください。",
        );
        reportAsyncError(e);
      }
    },
    [procedureId, afterReallocate],
  );

  return {
    currentPaths,
    pageStatus,
    newPath,
    newStartDatetime,
    isLoading,
    onChangePath,
    onChangeStartDate,
    onConfirm,
    onBackToEdit,
    onReallocateCustomPath,
    onDeleteReservedCustomPath,
    usingPathProcName: usingPathProc?.name,
    canAssign,
    reservedPathProcName: reservedPathProc?.name,
    isReserved: !!reservedPathProc && reservedPathProc.id !== procedureId,
  };
};
